import React, { Component } from 'react';
import moment from 'moment';
import CalendarPopover from './CalendarPopover';
import { isAllLocations, getLocationBadgeColor } from '../../../util/locationUtils';
import { getTechnicianColor, getStageColor, getEventBands, buildBandGradient, getWashFromAccent } from '../../../util/colorCodingUtils';
import './CalendarGrid.css';

const HOURS = [
    '8 AM', '9 AM', '10 AM', '11 AM', '12 PM',
    '1 PM', '2 PM', '3 PM', '4 PM', '5 PM'
];

class CalendarGrid extends Component {
    state = {
        popoverJob: null,
        popoverPosition: { top: 0, left: 0 },
        morePopover: null, // { dateStr, jobs, top, left }
    };

    // ---- Data helpers ----

    groupJobsByDate = () => {
        const map = {};
        (this.props.displayWorkTable || []).forEach(job => {
            const key = job.startDate; // YYYY-MM-DD
            if (!map[key]) map[key] = [];
            map[key].push(job);
        });
        return map;
    };

    getDateColumns = () => {
        const { range, startDate } = this.props;
        const start = moment(startDate);
        if (range === 'day') return [start.format('YYYY-MM-DD')];
        // week — 7 days starting from startDate (already start-of-week)
        const cols = [];
        for (let i = 0; i < 7; i++) {
            cols.push(moment(start).add(i, 'days').format('YYYY-MM-DD'));
        }
        return cols;
    };

    // Holidays/closures are calendar-date semantic regardless of how the server
    // serializes them (date-only "YYYY-MM-DD" or full UTC ISO "YYYY-MM-DDT00:00:00Z").
    // Strip to the YYYY-MM-DD prefix and compare as strings — avoids the prior
    // bug where moment.utc().local() shifted the date backwards in HST/PST.
    static toCalendarDate(d) {
        return d ? String(d).substring(0, 10) : '';
    }

    isDateBlocked = (dateStr) => {
        return (this.props.blockedPeriodsInRange || []).some(p => {
            const pStart = CalendarGrid.toCalendarDate(p.start_date);
            const pEnd = CalendarGrid.toCalendarDate(p.end_date);
            return dateStr >= pStart && dateStr <= pEnd;
        });
    };

    getBlockedReason = (dateStr) => {
        const match = (this.props.blockedPeriodsInRange || []).find(p => {
            const pStart = CalendarGrid.toCalendarDate(p.start_date);
            const pEnd = CalendarGrid.toCalendarDate(p.end_date);
            return dateStr >= pStart && dateStr <= pEnd;
        });
        return match ? match.reason : '';
    };

    // ---- Popover ----

    handleBlockClick = (e, job) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        this.setState({
            popoverJob: job,
            popoverPosition: {
                top: rect.bottom + 4,
                left: rect.left,
            },
            morePopover: null,
        });
    };

    handleClosePopover = () => {
        this.setState({ popoverJob: null });
    };

    handleMoreClick = (e, dateStr, jobs) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        this.setState({
            morePopover: {
                dateStr,
                jobs,
                top: rect.bottom + 4,
                left: rect.left,
            },
        });
    };

    handleCloseMore = () => {
        this.setState({ morePopover: null });
    };

    // ---- Render helpers ----

    // Convert "HH:MM" or "HH:MM:SS" to a decimal hour-of-day (e.g., "09:30" → 9.5).
    static parseTimeToHour(t) {
        if (!t || typeof t !== 'string') return null;
        const [h, m] = t.split(':').map(n => parseInt(n, 10));
        if (Number.isNaN(h)) return null;
        return h + (Number.isFinite(m) ? m / 60 : 0);
    }

    // Compute absolute-position style for a time-anchored job inside cg-day-col
    // (which is already position:relative). Returns null if start_time/end_time
    // are missing or unparseable — caller falls back to flex-stacked rendering.
    static computeTimePosition(job) {
        const startHour = CalendarGrid.parseTimeToHour(job.start_time);
        const endHour = CalendarGrid.parseTimeToHour(job.end_time);
        if (startHour == null || endHour == null) return null;
        const GRID_START_HOUR = 8;   // matches HOURS[0] = 8 AM
        const GRID_SPAN_HOURS = 10;  // HOURS covers 8 AM → 6 PM (5 PM slot ends at 6 PM)
        const topPct = ((startHour - GRID_START_HOUR) / GRID_SPAN_HOURS) * 100;
        const durationHours = Math.max(0.25, endHour - startHour);
        const heightPct = (durationHours / GRID_SPAN_HOURS) * 100;
        return {
            position: 'absolute',
            top: `${Math.max(0, topPct)}%`,
            height: `${heightPct}%`,
            left: 4,
            right: 4,
            zIndex: 2,
        };
    }

    renderJobBlock = (job, flexGrow, positionStyle = null) => {
        const isDealer = job.requestType === 'dealer';
        const showLoc = isAllLocations(this.props.selectedLocationId) &&
            Array.isArray(this.props.locations) && this.props.locations.length > 1;
        const locId = job.location_id || job.locationId;

        const vehicle = [job.vehicleYear, job.vehicleMake, job.vehicleModel]
            .filter(Boolean).join(' ') || 'Vehicle';
        const service = [job.category, job.serviceDescription].filter(Boolean).join(' - ') || 'Service';
        const customer = job.customerName || job.dealerName || '';

        const state = job.currentState || '';
        const isStarted = state === 'started';
        const isFinished = state === 'finished';
        const isCollected = state === 'collected';

        const { colorMode, stageColors, technicians, selectedTechnician } = this.props;
        let jobColor = null;
        let bands = null;
        const ta = job.tech_assignment;
        const isMultiTech = ta && (ta.effective_state === 'mixed' || ta.effective_state === 'partial');
        const techFilterActive = selectedTechnician && selectedTechnician !== 'All Technicians';

        if (colorMode === 'stage') {
            jobColor = getStageColor(state, stageColors);
        } else if (colorMode === 'technician') {
            if (isMultiTech && techFilterActive) {
                const me = (ta.techs || []).find(t => t.name === selectedTechnician);
                if (me?.user_color) {
                    jobColor = { wash: getWashFromAccent(me.user_color), accent: me.user_color };
                } else {
                    jobColor = { wash: '#F3F4F6', accent: '#9CA3AF' };
                }
            } else if (isMultiTech) {
                bands = getEventBands(ta, technicians);
                jobColor = { wash: '#F3F4F6', accent: '#9CA3AF' };
            } else {
                const techKey = job.assigned_technician_id || job.assignedTechnicianId || job.assignedTechnicianName;
                if (techKey) {
                    jobColor = getTechnicianColor(techKey, technicians);
                } else {
                    jobColor = { wash: '#F3F4F6', accent: '#9CA3AF' };
                }
            }
        }
        const blockStyle = positionStyle
            ? { ...positionStyle }
            : { flex: `${flexGrow || 1} 1 0` };
        if (jobColor) {
            blockStyle.background = jobColor.wash;
            blockStyle.borderLeft = `3px solid ${jobColor.accent}`;
        }
        if (bands && bands.segments) {
            const gradient = buildBandGradient(bands.segments);
            if (gradient) {
                blockStyle.borderLeft = '3px solid transparent';
                blockStyle.borderImage = `${gradient} 1`;
                blockStyle.borderImageSlice = 1;
            }
        }
        const typeClass = jobColor ? '' : (isDealer ? 'dealer' : 'installer');

        // Compute assignee text for the per-block tech line.
        let assigneeText = job.assignedTechnicianName;
        if (isMultiTech && !techFilterActive) {
            const techNames = (ta.techs || []).map(t => t.name).filter(Boolean);
            if (techNames.length === 1) assigneeText = techNames[0];
            else if (techNames.length === 2) assigneeText = techNames.join(', ');
            else if (techNames.length >= 3) assigneeText = 'Multiple techs';
        }

        return (
            <div
                key={job.viewKey}
                className={`cg-job-block ${typeClass}`}
                style={blockStyle}
                onClick={(e) => this.handleBlockClick(e, job)}
                title={`${service} — ${vehicle}`}
            >
                <div className="cg-job-vehicle">{vehicle}</div>
                <div className="cg-job-service">{service}</div>
                {showLoc && locId && (
                    <span
                        className="cg-loc-badge"
                        style={{ backgroundColor: getLocationBadgeColor(locId, this.props.locations) }}
                    >
                        {job.locationName || job.location_name || ''}
                    </span>
                )}
                <div className="cg-job-customer">{customer}</div>
                {assigneeText && (
                    <div className="cg-job-tech">
                        <i className="fa-sharp fa-regular fa-user" style={{ fontSize: 9, marginRight: 3 }} />
                        {assigneeText}{bands && bands.overflow > 0 ? ` +${bands.overflow}` : ''}
                    </div>
                )}
                <div className="cg-job-meta">
                    {isStarted && (
                        <span className="cg-job-status-badge started">In Progress</span>
                    )}
                    {isFinished && (
                        <span className="cg-job-status-badge completed">Finished</span>
                    )}
                    {isCollected && (
                        <span className="cg-job-status-badge completed">Collected</span>
                    )}
                    {job.isMultiDay && (
                        <span className="cg-job-day-badge">Day {job.currentDay}/{job.totalDays}</span>
                    )}
                </div>
            </div>
        );
    };

    renderMonthJobBar = (job) => {
        const isDealer = job.requestType === 'dealer';
        const vehicle = [job.vehicleMake, job.vehicleModel].filter(Boolean).join(' ') || 'Vehicle';
        const service = job.category || 'Service';
        const showLoc = isAllLocations(this.props.selectedLocationId) &&
            Array.isArray(this.props.locations) && this.props.locations.length > 1;
        const locId = job.location_id || job.locationId;

        const { colorMode, stageColors, technicians } = this.props;
        let jobColor = null;
        if (colorMode === 'stage') {
            jobColor = getStageColor(job.currentState || '', stageColors);
        } else if (colorMode === 'technician') {
            const techKey = job.assigned_technician_id || job.assignedTechnicianId || job.assignedTechnicianName;
            if (techKey) {
                jobColor = getTechnicianColor(techKey, technicians);
            } else {
                jobColor = { wash: '#F3F4F6', accent: '#9CA3AF' };
            }
        }
        const barStyle = jobColor ? { background: jobColor.accent } : {};
        const typeClass = jobColor ? '' : (isDealer ? 'dealer' : 'installer');

        return (
            <div
                key={job.viewKey}
                className={`cg-month-bar ${typeClass}`}
                style={barStyle}
                onClick={(e) => this.handleBlockClick(e, job)}
                title={`${service} | ${vehicle}`}
            >
                {showLoc && locId && (
                    <span
                        className="cg-month-bar-loc-dot"
                        style={{ backgroundColor: getLocationBadgeColor(locId, this.props.locations) }}
                    />
                )}
                <span className="cg-month-bar-text">{service} | {vehicle}</span>
            </div>
        );
    };

    // ---- Day/Week grid ----

    renderDayWeekGrid = () => {
        const columns = this.getDateColumns();
        const jobsByDate = this.groupJobsByDate();
        const todayStr = moment().format('YYYY-MM-DD');

        return (
            <div className="calendar-grid-view">
                {/* Header */}
                <div className="cg-header">
                    <div className="cg-time-spacer" />
                    {columns.map(dateStr => {
                        const m = moment(dateStr);
                        const isToday = dateStr === todayStr;
                        return (
                            <div key={dateStr} className={`cg-day-header ${isToday ? 'cg-today' : ''}`}>
                                <div className="cg-day-name">{m.format('ddd')}</div>
                                <div className="cg-day-number">{m.format('D')}</div>
                            </div>
                        );
                    })}
                </div>

                {/* Body */}
                <div className="cg-body">
                    {/* Time column */}
                    <div className="cg-time-col">
                        {HOURS.map(h => (
                            <div key={h} className="cg-time-slot">{h}</div>
                        ))}
                    </div>

                    {/* Day columns */}
                    {columns.map(dateStr => {
                        const isToday = dateStr === todayStr;
                        const blocked = this.isDateBlocked(dateStr);
                        const reason = blocked ? this.getBlockedReason(dateStr) : '';
                        const dayJobs = jobsByDate[dateStr] || [];

                        return (
                            <div key={dateStr} className={`cg-day-col ${isToday ? 'cg-today-col' : ''}`}>
                                {/* Hour grid lines */}
                                {HOURS.map(h => (
                                    <div key={h} className="cg-hour-line" />
                                ))}

                                {/* Blocked overlay */}
                                {blocked && (
                                    <div className="cg-blocked-overlay">
                                        <div className="cg-blocked-text">
                                            Closed{reason ? ` — ${reason}` : ''}
                                        </div>
                                    </div>
                                )}

                                {/* Jobs — mobile time-anchored ones get absolute
                                    positioning on the hour rail; everything else
                                    falls back to flex-stacked. */}
                                {!blocked && dayJobs.length > 0 && (() => {
                                    const timedJobs = [];
                                    const untimedJobs = [];
                                    dayJobs.forEach(j => {
                                        const pos = CalendarGrid.computeTimePosition(j);
                                        if (pos) timedJobs.push({ job: j, pos });
                                        else untimedJobs.push(j);
                                    });
                                    return (
                                        <>
                                            {timedJobs.map(({ job, pos }) =>
                                                this.renderJobBlock(job, 1, pos)
                                            )}
                                            {untimedJobs.length > 0 && (
                                                <div className="cg-jobs-container">
                                                    {untimedJobs.map(job => {
                                                        const duration = Math.max(job.estimate || 0.5, 0.1);
                                                        return this.renderJobBlock(job, duration);
                                                    })}
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}

                                {!blocked && dayJobs.length === 0 && (
                                    <div className="cg-empty-day">—</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // ---- Month grid ----

    renderMonthGrid = () => {
        const { startDate } = this.props;
        const monthStart = moment(startDate).startOf('month');
        const monthEnd = moment(startDate).endOf('month');
        const calStart = moment(monthStart).startOf('week');  // Sunday before month
        const calEnd = moment(monthEnd).endOf('week');         // Saturday after month
        const todayStr = moment().format('YYYY-MM-DD');

        const jobsByDate = this.groupJobsByDate();
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        // Build array of all dates to show
        const cells = [];
        let cursor = moment(calStart);
        while (cursor.isSameOrBefore(calEnd, 'day')) {
            cells.push(cursor.format('YYYY-MM-DD'));
            cursor.add(1, 'day');
        }

        return (
            <div className="calendar-grid-view">
                <div className="cg-month">
                    {/* Header row */}
                    {dayNames.map(d => (
                        <div key={d} className="cg-month-header-cell">{d}</div>
                    ))}

                    {/* Day cells */}
                    {cells.map(dateStr => {
                        const m = moment(dateStr);
                        const isOutside = m.month() !== monthStart.month();
                        const isToday = dateStr === todayStr;
                        const blocked = this.isDateBlocked(dateStr);
                        const reason = blocked ? this.getBlockedReason(dateStr) : '';
                        const dayJobs = jobsByDate[dateStr] || [];
                        const maxVisible = 3;
                        const extraCount = dayJobs.length - maxVisible;

                        let cellClass = 'cg-month-cell';
                        if (isOutside) cellClass += ' cg-outside';
                        if (isToday) cellClass += ' cg-today-cell';
                        if (blocked) cellClass += ' cg-blocked-cell';

                        return (
                            <div key={dateStr} className={cellClass}>
                                <div>
                                    <span className="cg-month-date">{m.format('D')}</span>
                                    {blocked && <span className="cg-month-closed-label">{reason || 'Closed'}</span>}
                                </div>
                                {!blocked && dayJobs.slice(0, maxVisible).map(job => this.renderMonthJobBar(job))}
                                {!blocked && extraCount > 0 && (
                                    <div
                                        className="cg-month-more"
                                        onClick={(e) => this.handleMoreClick(e, dateStr, dayJobs.slice(maxVisible))}
                                    >
                                        +{extraCount} more
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    render() {
        const { range } = this.props;
        const { popoverJob, popoverPosition, morePopover } = this.state;

        return (
            <div style={{ position: 'relative' }}>
                {(range === 'day' || range === 'week') && this.renderDayWeekGrid()}
                {range === 'month' && this.renderMonthGrid()}

                {/* Job popover */}
                {popoverJob && (
                    <CalendarPopover
                        job={popoverJob}
                        position={popoverPosition}
                        onClose={this.handleClosePopover}
                        onViewDetails={this.props.onDisplayDetails}
                        locations={this.props.locations}
                        selectedLocationId={this.props.selectedLocationId}
                        colorMode={this.props.colorMode}
                        stageColors={this.props.stageColors}
                        technicians={this.props.technicians}
                    />
                )}

                {/* "+N more" popover for month view */}
                {morePopover && (
                    <div
                        className="cg-more-popover"
                        style={{
                            position: 'fixed',
                            top: morePopover.top,
                            left: morePopover.left,
                            zIndex: 100,
                        }}
                    >
                        {morePopover.jobs.map(job => this.renderMonthJobBar(job))}
                        <div
                            className="cg-month-more"
                            onClick={this.handleCloseMore}
                            style={{ textAlign: 'center', marginTop: 4 }}
                        >
                            Close
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default CalendarGrid;
